import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useMutation } from '@apollo/client';
import axios from 'axios';

import { CREATE_USER, SIGNUP_WITH_TWITTER } from '../../http/apollo-queries';

import { useSession } from '../../context/Session';
import { useSignUpContext } from './SignUpContext';
import { useAuth } from '../../context/Auth';
import { useModalContext } from '../../context/Modal';

import { useUrlQuery } from '../../hooks/use-url-query';

import { Button, Loading } from '../../components/UI';

import { trackEvent } from '../../helpers/analytics';
import { logger } from '../../helpers/logger';

const SignUpWithTwitter = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const session = useSession();
  const query = useUrlQuery();
  const { state } = useSignUpContext();
  const { signIn, refresh, refetch: refetchUser } = useAuth();
  const { dispatch: setModal, closeModal } = useModalContext();

  const apiPath = process.env.REACT_APP_TWITTER_API_URI;
  const callbackUrl = window.location.origin + '/signup';

  const [signupWithTwitter] = useMutation<{
    signupWithTwitter: { otpPassword: string };
  }>(SIGNUP_WITH_TWITTER);
  const [createUser] = useMutation(CREATE_USER);

  const handleClick = () => {
    trackEvent('signup_twitter_initiated');

    //logger.log('SignUpWithTwitter:Step1');

    // dispatch({
    //   type: 'SET_LOADING',
    //   payload: true,
    // });
    setModal({
      isFullScreenElement: true,
      className: 'bg-grey-90 bg-opacity-10',
      element: (
        <Loading
          className="w-32 h-16 m-auto text-primary"
          size="large"
          status
        />
      ),
    });

    axios({
      url: `${apiPath}/oauth/request_token`,
      data: { callbackUrl },
      method: 'POST',
    })
      .then((res) => {
        logger.log('SignUpWithTwitter:authenticate', res);
        const { oauth_token, oauth_token_secret } = res.data;

        session.setSessionItem('oauth_token', oauth_token);
        session.setSessionItem('oauth_token_secret', oauth_token_secret);

        window.location.href = `https://api.twitter.com/oauth/authenticate?oauth_token=${oauth_token}`;
      })
      .catch((err) => {
        logger.error('SignUpWithTwitter', err);
      })
      .finally(closeModal);
  };

  useEffect(() => {
    const oauth_token = query.get('oauth_token');
    const oauth_verifier = query.get('oauth_verifier');

    const oauth_token_secret = session.getSessionItem('oauth_token_secret');
    const oauth_access_token = session.getSessionItem('oauth_access_token');
    // const oauth_access_token_secret = session.getSessionItem(
    //   'oauth_access_token_secret'
    // );

    if (
      oauth_token &&
      oauth_verifier &&
      oauth_token_secret &&
      !oauth_access_token
    ) {
      //logger.log('SignUpWithTwitter:Step2');
      setModal({
        isFullScreenElement: true,
        className: 'bg-grey-90 bg-opacity-10',
        element: (
          <Loading
            className="w-32 h-16 m-auto text-primary"
            size="large"
            status
          />
        ),
      });

      axios({
        url: `${apiPath}/oauth/access_token`,
        method: 'POST',
        data: { oauth_token, oauth_verifier, oauth_token_secret },
      })
        .then((res) => {
          logger.log('SignUpWithTwitter:getAccessToken', res.data);

          const { oauth_access_token, oauth_access_token_secret } = res.data;

          session.setSessionItem('oauth_access_token', oauth_access_token);
          session.setSessionItem(
            'oauth_access_token_secret',
            oauth_access_token_secret
          );

          //logger.log('SignUpWithTwitter:Step3');

          //Authenticated Resource Access
          axios({
            url: `${apiPath}/oauth/profile`,
            method: 'POST',
            data: { oauth_access_token, oauth_access_token_secret },
            headers: {
              'Access-Control-Request-Method': 'POST',
            },
          })
            .then((res) => {
              const { id, email } = res.data;

              logger.log('SignUpWithTwitter:getProfile', res.data);

              signupWithTwitter({
                variables: {
                  email,
                  twitterId: id,
                  token: oauth_access_token,
                },
              })
                .then((res) => {
                  const otpPassword =
                    res?.data?.signupWithTwitter?.otpPassword || '';
                  return signIn(email, otpPassword);
                })
                .then(() => {
                  return createUser({
                    variables: {
                      language: i18n.language.toUpperCase(),
                      referralCode: state.referralCode,
                    },
                  });
                })
                .then(() => refresh())
                .then(() => refetchUser())
                .then(() => navigate('/choose-username'))
                .catch((err) => {
                  logger.error(err);
                  toast.error(
                    t('signUp.form.signupWithTwitterError.' + err.message)
                  );
                })
                .finally(closeModal);
            })
            .finally(closeModal);
        })
        .catch((err) => {
          logger.error(err);
        })
        .finally(closeModal);
    }
  }, []);

  return (
    <Button
      type="button"
      buttonClass="border border-grey-30 w-full font-semibold text-black dark:text-white mt-4"
      onClick={() => handleClick()}
    >
      {t('signUp.form.signupWithTwitter')}
      <img alt="twitter" src="/twitter.svg" className="ml-auto" />
    </Button>
  );
};

export default SignUpWithTwitter;
